import { useState, useEffect, useCallback } from "react";
// import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  axiosGetQuestions,
  axiosCreateQuestion,
  axiosUpdateQuestion,
  axiosDeleteQuestion,
  axiosGetCategories,
  axiosCreateCategory,
  axiosUpdateCategory,
  axiosDeleteCategory,
  axiosGetSkills,
  axiosCreateSkill,
  axiosUpdateSkill,
  axiosDeleteSkill,
  axiosGetLanguages,
  axiosCreateLanguage,
  axiosGetMasterTemplates,
  axiosCreateMasterTemplate,
  axiosUpdateMasterTemplate,
  axiosDeleteMasterTemplate,
  axiosGetCompanies,
} from "../../helpers/superAdminAxios";

const ContentManagement = () => {
  const { token, notify } = useOutletContext();
  const [activeTab, setActiveTab] = useState("questions");
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");

  // Data states
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [masterTemplates, setMasterTemplates] = useState([]);

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [localizationData, setLocalizationData] = useState({});

  // const { t } = useTranslation();

  const tabs = [
    { id: "questions", name: "Questions", icon: "❓" },
    { id: "categories", name: "Categories", icon: "📁" },
    { id: "skills", name: "Skills", icon: "🎯" },
    { id: "languages", name: "Languages", icon: "🌐" },
    { id: "templates", name: "Master Templates", icon: "📋" },
  ];

  const loadCompanies = useCallback(async () => {
    try {
      const response = await axiosGetCompanies({
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error loading companies:", error);
    }
  }, [token]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params =
        selectedCompany !== "all" ? { companyId: selectedCompany } : {};
      const headers = { Authorization: `Bearer ${token}` };

      switch (activeTab) {
        case "questions":
          const questionsResponse = await axiosGetQuestions({
            params,
            headers,
          });
          setQuestions(questionsResponse.data);
          break;
        case "categories":
          const categoriesResponse = await axiosGetCategories({
            params,
            headers,
          });
          setCategories(categoriesResponse.data);
          break;
        case "skills":
          const skillsResponse = await axiosGetSkills({
            params,
            headers,
          });
          setSkills(skillsResponse.data);
          break;
        case "languages":
          const languagesResponse = await axiosGetLanguages({
            headers,
          });
          setLanguages(languagesResponse.data);
          break;
        case "templates":
          const templatesResponse = await axiosGetMasterTemplates({
            headers,
          });
          setMasterTemplates(templatesResponse.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error loading ${activeTab}:`, error);
      notify(`Failed to load ${activeTab}`, "error");
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, selectedCompany, token, notify]);

  const loadLanguages = useCallback(async () => {
    try {
      const response = await axiosGetLanguages({
        headers: { Authorization: `Bearer ${token}` },
      });
      setLanguages(response.data);
    } catch (error) {
      console.error("Error loading languages:", error);
    }
  }, [token]);

  useEffect(() => {
    loadCompanies();
    loadLanguages();
    loadData();
  }, [loadCompanies, loadLanguages, loadData]);

  useEffect(() => {
    loadData();
  }, [activeTab, selectedCompany, loadData]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);

    // Set up localization data
    if (item.localizations && item.localizations.length > 0) {
      const localizationMap = {};
      item.localizations.forEach((loc) => {
        localizationMap[loc.locale] = {
          title: loc.title || "",
          text: loc.text || "",
          summary: loc.summary || "",
          text_self: loc.text_self || "",
        };
      });
      setLocalizationData(localizationMap);
    } else {
      setLocalizationData({});
    }

    setSelectedLanguage("en");
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        switch (activeTab) {
          case "questions":
            await axiosDeleteQuestion({ url: `/${id}`, headers });
            break;
          case "categories":
            await axiosDeleteCategory({ url: `/${id}`, headers });
            break;
          case "skills":
            await axiosDeleteSkill({ url: `/${id}`, headers });
            break;
          case "templates":
            await axiosDeleteMasterTemplate({ url: `/${id}`, headers });
            break;
          default:
            break;
        }

        notify(`${activeTab.slice(0, -1)} deleted successfully`, "success");
        loadData();
      } catch (error) {
        console.error(`Error deleting ${activeTab.slice(0, -1)}:`, error);
        notify(`Failed to delete ${activeTab.slice(0, -1)}`, "error");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Prepare data with localizations
      const dataToSend = { ...formData };
      if (Object.keys(localizationData).length > 0) {
        dataToSend.localizations = Object.entries(localizationData).map(
          ([locale, data]) => ({
            locale,
            ...data,
          })
        );
      }

      let data = { data: dataToSend };

      // Handle file uploads for categories and skills (both create and update)
      if (
        (activeTab === "categories" || activeTab === "skills") &&
        (formData.iconFile || formData.categoryImageFile)
      ) {
        const formDataToSend = new FormData();
        formDataToSend.append("data", JSON.stringify(dataToSend));

        if (formData.iconFile) {
          formDataToSend.append("icon", formData.iconFile);
        }
        if (formData.categoryImageFile) {
          formDataToSend.append("category_image", formData.categoryImageFile);
        }

        data = formDataToSend;
        // Don't set Content-Type manually for FormData - axios will set it with boundary
      }

      switch (activeTab) {
        case "questions":
          editingItem
            ? await axiosUpdateQuestion({
                url: `/${editingItem.id}`,
                data,
                headers,
              })
            : await axiosCreateQuestion({ data, headers });
          break;
        case "categories":
          editingItem
            ? await axiosUpdateCategory({
                url: `/${editingItem.id}`,
                data,
                headers,
              })
            : await axiosCreateCategory({ data, headers });
          break;
        case "skills":
          editingItem
            ? await axiosUpdateSkill({
                url: `/${editingItem.id}`,
                data,
                headers,
              })
            : await axiosCreateSkill({ data, headers });
          break;
        case "languages":
          await axiosCreateLanguage({ data, headers });
          break;
        case "templates":
          editingItem
            ? await axiosUpdateMasterTemplate({
                url: `/${editingItem.id}`,
                data,
                headers,
              })
            : await axiosCreateMasterTemplate({ data, headers });
          break;
        default:
          break;
      }

      notify(
        `${activeTab.slice(0, -1)} ${
          editingItem ? "updated" : "created"
        } successfully`,
        "success"
      );
      setShowForm(false);
      setFormData({});
      setEditingItem(null);
      setLocalizationData({});
      loadData();
    } catch (error) {
      console.error(
        `Error ${editingItem ? "updating" : "creating"} ${activeTab.slice(
          0,
          -1
        )}:`,
        error
      );
      notify(
        `Failed to ${editingItem ? "update" : "create"} ${activeTab.slice(
          0,
          -1
        )}`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultFormData = () => {
    const base = {
      company_id: selectedCompany !== "all" ? selectedCompany : null,
    };

    switch (activeTab) {
      case "questions":
        return { ...base, text: "", type: "Scale", text_self: "" };
      case "categories":
        return {
          ...base,
          title: "",
          text: "",
          color: "",
          text_color: "",
          summary: "",
          palette: "light-blue",
        };
      case "skills":
        return {
          ...base,
          title: "",
          text: "",
          color: "",
          text_color: "",
          summary: "",
          palette: "light-blue",
        };
      case "languages":
        return { name: "", code: "" };
      case "templates":
        return { title: "", categories: [], tags: [] };
      default:
        return {};
    }
  };

  const renderForm = () => {
    if (!showForm) return null;

    const showLanguageTabs = ["questions", "categories", "skills"].includes(
      activeTab
    );

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingItem ? "Edit" : "Create"} {activeTab.slice(0, -1)}
            </h3>

            {showLanguageTabs && (
              <div className="mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => setSelectedLanguage(language.code)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          selectedLanguage === language.code
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        {language.name} ({language.code.toUpperCase()})
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {renderFormFields()}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : editingItem ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const getLocalizedValue = (field) => {
    if (selectedLanguage === "en") {
      return formData[field] || "";
    }
    return localizationData[selectedLanguage]?.[field] || "";
  };

  const setLocalizedValue = (field, value) => {
    if (selectedLanguage === "en") {
      setFormData({ ...formData, [field]: value });
    } else {
      setLocalizationData({
        ...localizationData,
        [selectedLanguage]: {
          ...localizationData[selectedLanguage],
          [field]: value,
        },
      });
    }
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case "questions":
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Question Text *{" "}
                {selectedLanguage !== "en" &&
                  `(${selectedLanguage.toUpperCase()})`}
              </label>
              <input
                type="text"
                value={getLocalizedValue("text")}
                onChange={(e) => setLocalizedValue("text", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required={selectedLanguage === "en"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type *
              </label>
              <select
                value={formData.type || "Scale"}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="Scale">Scale</option>
                <option value="Input">Input</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Self Text{" "}
                {selectedLanguage !== "en" &&
                  `(${selectedLanguage.toUpperCase()})`}
              </label>
              <input
                type="text"
                value={getLocalizedValue("text_self")}
                onChange={(e) => setLocalizedValue("text_self", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </>
        );
      case "categories":
      case "skills":
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title *{" "}
                {selectedLanguage !== "en" &&
                  `(${selectedLanguage.toUpperCase()})`}
              </label>
              <input
                type="text"
                value={getLocalizedValue("title")}
                onChange={(e) => setLocalizedValue("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required={selectedLanguage === "en"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Text{" "}
                {selectedLanguage !== "en" &&
                  `(${selectedLanguage.toUpperCase()})`}
              </label>
              <textarea
                value={getLocalizedValue("text")}
                onChange={(e) => setLocalizedValue("text", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Summary{" "}
                {selectedLanguage !== "en" &&
                  `(${selectedLanguage.toUpperCase()})`}
              </label>
              <textarea
                value={getLocalizedValue("summary")}
                onChange={(e) => setLocalizedValue("summary", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color || "#3B82F6"}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Text Color
                </label>
                <input
                  type="color"
                  value={formData.text_color || "#FFFFFF"}
                  onChange={(e) =>
                    setFormData({ ...formData, text_color: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Palette
              </label>
              <select
                value={formData.palette || "light-blue"}
                onChange={(e) =>
                  setFormData({ ...formData, palette: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="light-purple">Light Purple</option>
                <option value="light-brown">Light Brown</option>
                <option value="light-green">Light Green</option>
                <option value="light-blue">Light Blue</option>
                <option value="dark-red">Dark Red</option>
                <option value="dark-purple">Dark Purple</option>
                <option value="hot-orange">Hot Orange</option>
                <option value="sea-blue">Sea Blue</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Icon
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setFormData({ ...formData, iconFile: e.target.files[0] })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              {(formData.icon || formData.iconFile) && (
                <div className="mt-2">
                  <img
                    src={
                      formData.iconFile
                        ? URL.createObjectURL(formData.iconFile)
                        : `${process.env.REACT_APP_BACKEND_URL}${formData.icon.url}`
                    }
                    alt={
                      formData.iconFile ? "New icon preview" : "Current icon"
                    }
                    className="w-8 h-8 object-contain"
                  />
                  {formData.iconFile && (
                    <p className="text-xs text-green-600 mt-1">
                      New file selected
                    </p>
                  )}
                </div>
              )}
            </div>
            {activeTab === "categories" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      categoryImageFile: e.target.files[0],
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                {(formData.category_image || formData.categoryImageFile) && (
                  <div className="mt-2">
                    <img
                      src={
                        formData.categoryImageFile
                          ? URL.createObjectURL(formData.categoryImageFile)
                          : `${process.env.REACT_APP_BACKEND_URL}${formData.category_image.url}`
                      }
                      alt={
                        formData.categoryImageFile
                          ? "New category image preview"
                          : "Current category"
                      }
                      className="w-16 h-16 object-cover rounded"
                    />
                    {formData.categoryImageFile && (
                      <p className="text-xs text-green-600 mt-1">
                        New file selected
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        );
      case "languages":
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Language Name *
              </label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Language Code *
              </label>
              <input
                type="text"
                value={formData.code || ""}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="e.g., en, es, fr"
                required
              />
            </div>
          </>
        );
      case "templates":
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Template Title *
              </label>
              <input
                type="text"
                value={formData.title || ""}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderTable = () => {
    const data = getCurrentData();
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No {activeTab} found
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {getTableHeaders().map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item) => (
              <tr key={item.id}>
                {getTableCells(item).map((cell, index) => (
                  <td
                    key={index}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  >
                    {cell}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    Edit
                  </button>
                  {activeTab !== "languages" && (
                    <button
                      onClick={() =>
                        handleDelete(
                          item.id,
                          item.title || item.text || item.name
                        )
                      }
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "questions":
        return questions;
      case "categories":
        return categories;
      case "skills":
        return skills;
      case "languages":
        return languages;
      case "templates":
        return masterTemplates;
      default:
        return [];
    }
  };

  const getTableHeaders = () => {
    switch (activeTab) {
      case "questions":
        return ["Text", "Type", "Self Text", "Company"];
      case "categories":
        return ["Title", "Company", "Palette", "Created"];
      case "skills":
        return ["Title", "Company", "Palette", "Created"];
      case "languages":
        return ["Name", "Code", "Created"];
      case "templates":
        return ["Title", "Categories", "Created"];
      default:
        return [];
    }
  };

  const getTableCells = (item) => {
    switch (activeTab) {
      case "questions":
        return [
          item.text?.substring(0, 50) + (item.text?.length > 50 ? "..." : ""),
          item.type,
          item.text_self?.substring(0, 30) +
            (item.text_self?.length > 30 ? "..." : "") || "N/A",
          item.company_name || "Global",
        ];
      case "categories":
      case "skills":
        return [
          <div className="flex items-center space-x-2">
            {item.icon && (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${item.icon.url}`}
                alt={item.title}
                className="w-5 h-5 object-contain"
              />
            )}
            <span>{item.title}</span>
          </div>,
          item.company_name || "Global",
          item.palette,
          new Date(item.created_at).toLocaleDateString(),
        ];
      case "languages":
        return [
          item.name,
          item.code,
          new Date(item.created_at).toLocaleDateString(),
        ];
      case "templates":
        return [
          item.title,
          item.categories_count,
          new Date(item.created_at).toLocaleDateString(),
        ];
      default:
        return [];
    }
  };

  if (isLoading && !showForm) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="mt-2 text-gray-600">
          Manage questions, categories, skills, languages, and master templates
        </p>
      </div>

      {/* Company Filter */}
      {activeTab !== "languages" && activeTab !== "templates" && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Company
          </label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Companies</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Actions */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">
          {tabs.find((tab) => tab.id === activeTab)?.name} (
          {getCurrentData().length})
        </h2>
        <button
          onClick={handleCreate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
        >
          + Add {activeTab.slice(0, -1)}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg">{renderTable()}</div>

      {/* Form Modal */}
      {renderForm()}
    </div>
  );
};

export default ContentManagement;
